import { useEffect, useMemo } from "react"
import { AnalyticsEvents } from "~/util/analyticsEvents"
import { Link } from "react-router-dom"
import { AgencySortFieldEnum, BusinessTypeEnum } from "~/__generated__/graphql"
import { getFragmentData } from "~/__generated__"
import { useDocumentTitle } from "~/common/useDocumentTitle"
import { useTrackScreenEvent } from "~/common/useTrackScreenEvent"
import { useHeaderContext } from "~/common/HeaderContext"
import { rosterPath } from "~/common/paths"
import {
  InstagramIcon,
  TikTokIcon,
  YouTubeIcon,
} from "~/common/SocialMediaIcons"
import { FoundingAgencyBadge } from "~/components/FoundingAgencyBadge"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { MarketplaceGate } from "~/components/MarketplaceGate"
import { TalentAgenciesTeaser } from "~/components/MarketplaceTeasers"
import { Button } from "~/shadcn/ui/button"
import { Card, CardContent } from "~/shadcn/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/shadcn/ui/table"
import { Avatar } from "~/ui/Avatar"
import { CopyButton } from "~/ui/CopyButton"
import { InfiniteLoadMore } from "~/ui/InfiniteLoadMore"
import { friendlyNumber, numberDisplay } from "~/util/numbers"
import { trackEvent } from "~/util/tracking"
import { useAgencies } from "./useAgencies"
import { AgencySortProvider } from "./components/AgencySortContext"
import { SortableColumn } from "./components/SortableColumn"
import { AGENCY_FRAGMENT } from "./api"

import ExternalLinkIcon from "~/images/icons/external-link.svg?react"

export const TalentAgenciesScreen = () => {
  useDocumentTitle("Talent Agencies")
  const user = useCurrentUser()
  const { setHeaderText, setSubheaderText } = useHeaderContext()
  const {
    agencies,
    data,
    loading,
    sortField,
    sortDir,
    onSort,
    onLoadNextPage,
    pageInfo,
  } = useAgencies({
    businessType: BusinessTypeEnum.Talent,
    defaultSortField: AgencySortFieldEnum.InfluencerCount,
    // The server refuses the directory without Marketplace access (ZAN-921).
    skip: !user.canAccessMarketplacePages.value,
  })

  useEffect(() => {
    setHeaderText("Talent Agencies")
  }, [setHeaderText])

  useEffect(() => {
    setSubheaderText(undefined)
  }, [setSubheaderText])

  const locked = !user.canAccessMarketplacePages.value
  const agencyCount = data?.agencies.totalCount
  useTrackScreenEvent(
    "view_talent_agencies",
    useMemo(
      (): AnalyticsEvents["view_talent_agencies"] | null =>
        locked
          ? { locked: true }
          : agencyCount === undefined
          ? null
          : { count: agencyCount },
      [locked, agencyCount]
    )
  )

  // useEffect(() => {
  //   setSubheaderText(
  //     loading
  //       ? "Loading..."
  //       : `${numberDisplay(data?.agencies.totalCount)} Agencies`
  //   )
  // }, [setSubheaderText, loading, data?.agencies.totalCount])

  return (
    <MarketplaceGate teaser={<TalentAgenciesTeaser />}>
      <div className="h-full pb-4 relative select-none">
        <Card className="absolute left-0 right-0 top-0 bottom-4 overflow-auto">
          <CardContent className="flex-1 p-6 pt-4 flex flex-col">
            <Card className="absolute top-4 bottom-4 left-6 right-6 flex-grow overflow-auto">
              <Table>
                <TableHeader>
                  <AgencySortProvider
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={onSort}
                  >
                    <TableRow>
                      <TableHead sticky>
                        <SortableColumn field={AgencySortFieldEnum.Name}>
                          Name
                        </SortableColumn>
                      </TableHead>
                      <TableHead sticky>
                        <SortableColumn
                          field={AgencySortFieldEnum.InfluencerCount}
                        >
                          Influencers
                        </SortableColumn>
                      </TableHead>
                      <TableHead sticky>
                        <SortableColumn
                          field={AgencySortFieldEnum.TiktokFollowers}
                        >
                          <div className="flex gap-1 items-center">
                            <TikTokIcon className="w-[18px] h-[20px]" />
                            TT
                          </div>
                        </SortableColumn>
                      </TableHead>
                      <TableHead sticky>
                        <SortableColumn
                          field={AgencySortFieldEnum.InstagramFollowers}
                        >
                          <div className="flex gap-1 items-center">
                            <InstagramIcon className="w-[18px] h-[18px]" />
                            IG
                          </div>
                        </SortableColumn>
                      </TableHead>
                      <TableHead sticky>
                        <SortableColumn
                          field={AgencySortFieldEnum.YoutubeSubscribers}
                        >
                          <div className="flex gap-1 items-center">
                            <YouTubeIcon className="w-[19px] h-[13px]" />
                            YT
                          </div>
                        </SortableColumn>
                      </TableHead>
                      <TableHead sticky className="text-center">
                        Website
                      </TableHead>
                      <TableHead sticky>
                        <SortableColumn
                          field={AgencySortFieldEnum.ContactEmail}
                        >
                          Email
                        </SortableColumn>
                      </TableHead>
                      <TableHead sticky />
                    </TableRow>
                  </AgencySortProvider>
                </TableHeader>
                <TableBody>
                  {agencies.map((agencyF) => {
                    let agency = getFragmentData(AGENCY_FRAGMENT, agencyF)
                    return (
                      <TableRow key={agency.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar
                              src={agency.logoThumbnailUrl}
                              alt={agency.name || "avatar"}
                            />
                            <span>{agency.name}</span>
                            <FoundingAgencyBadge show={agency.foundingAgency} />
                          </div>
                        </TableCell>
                        <TableCell>
                          {agency.businessType === BusinessTypeEnum.Talent &&
                            numberDisplay(Number(agency.influencerCount))}
                        </TableCell>
                        <TableCell>
                          {agency.businessType === BusinessTypeEnum.Talent &&
                            friendlyNumber(Number(agency.tiktokFollowers), 0)}
                        </TableCell>
                        <TableCell>
                          {agency.businessType === BusinessTypeEnum.Talent &&
                            friendlyNumber(
                              Number(agency.instagramFollowers),
                              0
                            )}
                        </TableCell>
                        <TableCell>
                          {agency.businessType === BusinessTypeEnum.Talent &&
                            friendlyNumber(
                              Number(agency.youtubeSubscribers),
                              0
                            )}
                        </TableCell>
                        <TableCell className="text-center">
                          {agency.website && (
                            <Link
                              to={agency.website}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLinkIcon
                                width={15}
                                height={15}
                                className="mx-auto"
                              />
                            </Link>
                          )}
                        </TableCell>
                        <TableCell>
                          {agency.contactEmail && (
                            <CopyButton
                              textToCopy={agency.contactEmail}
                              successMessage={`"${agency.contactEmail}" copied to clipboard`}
                              label={agency.contactEmail}
                              className="w-full justify-between"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {agency.influencerCount > 0 && (
                            <Link
                              to={`${rosterPath({})}?agency=${agency.id}`}
                              onClick={() =>
                                trackEvent("view_agency", {
                                  agencyId: agency.id,
                                  agencyName: agency.name ?? "",
                                })
                              }
                            >
                              <Button variant="outline" size="sm">
                                View Roster
                              </Button>
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              <InfiniteLoadMore
                onEndReached={onLoadNextPage}
                canLoadMore={!loading && !!pageInfo?.hasNextPage}
                loading={loading && agencies.length > 0}
                className="p-4"
              />
              {loading && <div>Loading...</div>}
            </Card>
          </CardContent>
        </Card>
      </div>
    </MarketplaceGate>
  )
}
